import { IonPage, useIonModal } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useParams } from 'react-router-dom'

import { useGetUnitGenericDetails } from '@apps-common/apis/portal/unit'
import { useGetUnitCustomZones } from '@apps-common/apis/portal/zones'
import { ZoneListCard, ZoneListCardSkeleton } from '@apps-common/apps/portal/pages/units/pages/manage/pages/zones/components'
import { ZoneModifyModal } from '@apps-common/apps/portal/pages/units/pages/manage/pages/zones/modals/zone-modify-modal'
import { NoResultsAddCard, PullRefresh } from '@apps-common/components/common'
import { AccountNoTitle, PageTitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundLg } from '@apps-common/components/elements'
import { Header, LayoutContainer, LayoutPadding, VertSpacerXl } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { Breadcrumbs, Navbar } from '@apps-common/components/navigation'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { UNIT_STATUS_CANCELLED } from '@apps-common/config/unit-filter'
import { sortNumerically } from '@apps-common/helpers/array-sort'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { ROUTE_UNIT_ZONES } from '@apps-radionet-portal/config/private-routes'

export const UnitZonesPage: React.FC = () => {
  const { status, unitId } = useParams<{ status: string; unitId: string }>()
  const { data: unit, isLoading: unitIsLoading, isError: unitIsError } = useGetUnitGenericDetails({ status, unitId })

  const {
    data: zones,
    isLoading: zonesIsLoading,
    isError: zonesIsError,
    refetch: zonesRefetch
  } = useGetUnitCustomZones({
    config: {
      enabled: unitId !== undefined
    },
    unitId
  })

  const [present, dismiss] = useIonModal(ZoneModifyModal, {
    componentProps: {
      unitId
    },
    onDismiss: (data: string, role: string) => dismiss(data, role),
    enableBackdropDismiss: true
  })

  const loading = unitIsLoading || unitIsError || zonesIsLoading || zonesIsError

  const { accountNo } = unit || {}

  return (
    <IonPage>
      <Header classes='!max-w-[1200px]'>
        <Navbar showBack transparent icons={[]} />
        <Breadcrumbs route={ROUTE_UNIT_ZONES} />
        <div className='flex items-center justify-between'>
          <PageTitle classes='flex items-center justify-start gap-[10px]'>
            <span className='block'>Zone Descriptions</span>
            <AccountNoTitle text={`(Acc# ${accountNo})`} />
          </PageTitle>
          <ModalButton
            component={ZoneModifyModal}
            disabled={loading || status === UNIT_STATUS_CANCELLED.value}
            componentProps={{
              unitId
            }}
            enterAnimation={modalSlideUpEnterAnimation}
            leaveAnimation={modalSlideUpLeaveAnimation}
            classes='relative w-[40px] h-[40px] rounded-[20px] flex items-center justify-center rounded-3xl'
          >
            <ButtonRoundLg
              type='div'
              loading={loading}
              disabled={loading || status === UNIT_STATUS_CANCELLED.value}
              className='action-success'
              tooltip={{ id: 'device-zones-add-new', place: 'bottom', message: 'Add new zone' }}
            >
              <commonIcons.add className='!text-[1.1rem]' />
            </ButtonRoundLg>
          </ModalButton>
        </div>
      </Header>
      <LayoutContainer>
        <LayoutPadding classes='pt-[20px] md:pt-[20px]'>
          <PullRefresh loading={false} callback={() => zonesRefetch()} />
          <div className='max-w-full sm:max-w-[1000px]'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-[15px]'>
              <AnimatePresence mode='popLayout'>
                {loading &&
                  Array.from(Array(4).keys()).map((z, i) => (
                    <motion.div key={`unit-zone-skeleton-${i}`} {...motionFadeIn(0.25)}>
                      <ZoneListCardSkeleton />
                    </motion.div>
                  ))}
                {!loading && zones && zones.length === 0 && (
                  <motion.div key='no-zones-create-button' {...motionFadeIn(0.25)}>
                    <NoResultsAddCard
                      message='Add a new zone description.'
                      disabled={loading || status === UNIT_STATUS_CANCELLED.value}
                      callback={() =>
                        present({
                          enterAnimation: modalSlideUpEnterAnimation,
                          leaveAnimation: modalSlideUpLeaveAnimation
                        })
                      }
                    />
                  </motion.div>
                )}
                {!loading &&
                  zones &&
                  zones.length > 0 &&
                  zones
                    ?.sort((a, b) => sortNumerically(a, b, 'unitCustomZoneDescriptionId'))
                    .map((z, i) => (
                      <motion.div key={`unit-zone-${z.unitCustomZoneDescriptionId}`} {...motionFadeIn(0.25)}>
                        <ZoneListCard unitZone={z} />
                      </motion.div>
                    ))}
              </AnimatePresence>
            </div>
          </div>
        </LayoutPadding>
        <VertSpacerXl />
      </LayoutContainer>
    </IonPage>
  )
}
